import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Dropdown, Tag, Tooltip, Switch, Popconfirm } from "antd";
import {
    EditTwoTone,
    DeleteTwoTone,
    EyeTwoTone,
    HistoryOutlined,
    MoreOutlined,
    DownloadOutlined,
    CopyOutlined,
    SwapOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import "./Questions.scss";
import SubHeader from "../../components/SubHeader/SubHeader";
import CustomTable from "../../components/CustomTable/CustomTable";
import CustomModal from "../../components/CustomModal/CustomModal";
import SelectInput from "../../components/SelectInput/SelectInput";
import SearchInput from "../../components/SearchInput/SearchInput";
import ActionGroup from "../../components/ActionGroup/ActionGroup";
import ActionIcon from "../../components/ActionIcon/ActionIcon";
import ButtonComponent from "../../components/ButtonComponent/ButtonComponent";
import Loader from "../../components/Loader/Loader";
import QuestionFormModal from "./QuestionFormModal";
import QuestionDetailView from "./QuestionDetailView";
import QuestionDetailDrawer from "./QuestionDetailDrawer";
import { errorToast, successToast } from "../../services/ToastHelper";
import { getPermissionForModule } from "../../services/permissions";
import {
    DELETE_QUESTION,
    DELETE_QUESTIONS_BULK,
    GET_MODULES_BY_GRADE,
    GET_QUESTIONS_BY_MODULE,
    GET_SYLLABUS,
    UPDATE_QUESTION,
} from "../../utils/apiPath";
import { deleteApi, postApi } from "../../utils/apiService";
import {
    QUESTION_TYPES,
    answerSummary,
    buildQuestionPayload,
    flattenModules,
    formatDateTime,
    getAudit,
    getQuestionId,
    getTypeLabel,
    hasAuditFields,
    sortRows,
    toExportRows,
} from "./questionHelpers";

const RECENT_DAYS = 7;

const Questions = () => {
    const { id, gradeId, moduleId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const { assessmentTypeName, subject, gradeName, moduleName } = location.state || {};
    const assessmentLabel = subject ? `${assessmentTypeName} (${subject})` : assessmentTypeName;
    const currentSubTitle = [assessmentLabel, gradeName, moduleName].filter(Boolean).join(" > ");

    const role = sessionStorage.getItem("role");
    const isAdmin = role === "Admin";
    const questionsPermission = getPermissionForModule("Assessment Questions");

    const [isLoading, setIsLoading] = useState(false);
    const [questionsData, setQuestionsData] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [syllabusOptions, setSyllabusOptions] = useState([]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [selectedKeys, setSelectedKeys] = useState([]);

    // Add / edit / duplicate
    const [formState, setFormState] = useState({ open: false, mode: "add", data: null });
    // Details + history drawer
    const [drawer, setDrawer] = useState({ open: false, question: null, tab: "details" });
    // Move to another module
    const [moveState, setMoveState] = useState({ open: false, rows: [], targetModuleId: null, saving: false });

    // Filters live in the URL so a filtered view can be bookmarked or shared.
    const filters = useMemo(
        () => ({
            moduleId: moduleId || searchParams.get("module") || "",
            syllabusId: searchParams.get("syllabus") || "",
            questionType: searchParams.get("type") || "",
            search: searchParams.get("q") || "",
            recentOnly: searchParams.get("changed") === "1",
        }),
        [searchParams, moduleId]
    );

    const setFilter = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value === null || value === undefined || value === false) next.delete(key);
        else next.set(key, String(value));
        setSearchParams(next, { replace: true });
        setPageIndex(0);
    };

    const syllabusNameById = useMemo(
        () => Object.fromEntries(syllabusOptions.map((s) => [s.value, s.label])),
        [syllabusOptions]
    );

    const fetchModules = async () => {
        const { statusCode, data } = await postApi(GET_MODULES_BY_GRADE, {
            gradeId,
            assessmentTypeId: id,
        });
        if (statusCode === 200) {
            const list = (data || []).map((m) => ({ label: m.name, value: m.id }));
            setModuleOptions(list);
            return list;
        }
        return [];
    };

    const fetchSyllabusOptions = async () => {
        const { statusCode, data } = await postApi(GET_SYLLABUS, {
            assessmentTypeId: id,
            gradeId,
        });
        if (statusCode === 200) {
            setSyllabusOptions((data || []).map((s) => ({ label: s.name, value: s.id })));
        }
    };

    /** One call covers every module: GetAssessmentTemplate takes a list of module ids. */
    const fetchQuestions = async (modules = moduleOptions) => {
        setIsLoading(true);
        try {
            const moduleIds = moduleId ? [moduleId] : modules.map((m) => m.value);
            if (!moduleId && moduleIds.length === 0) {
                setQuestionsData([]);
                return;
            }
            const { statusCode, data, message } = await postApi(GET_QUESTIONS_BY_MODULE, {
                gradeId,
                moduleIds,
                id,
            });
            if (statusCode === 200) {
                setQuestionsData(sortRows(flattenModules(data?.modules || [])));
            } else {
                errorToast(message);
            }
        } catch (err) {
            console.error(err);
            errorToast("Failed to fetch questions");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!id || !gradeId) return;
        (async () => {
            const modules = await fetchModules();
            fetchSyllabusOptions();
            await fetchQuestions(modules);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, gradeId, moduleId]);

    const reload = async () => {
        setSelectedKeys([]);
        await fetchQuestions();
    };

    /* ----------------------------- filtering ----------------------------- */

    // History always works (the endpoint is live); the Updated column needs the audit
    // fields on GetAssessmentTemplate, so it appears only once the server sends them.
    const auditFieldsPresent = useMemo(() => hasAuditFields(questionsData), [questionsData]);

    const recentCutoff = useMemo(() => dayjs().subtract(RECENT_DAYS, "day"), []);

    const filteredRows = useMemo(() => {
        const search = filters.search.trim().toLowerCase();
        return questionsData.filter((q) => {
            if (filters.moduleId && String(q.moduleId) !== String(filters.moduleId)) return false;
            if (filters.syllabusId && String(q.syllabusId) !== String(filters.syllabusId)) return false;
            if (filters.questionType && String(q.questionType) !== String(filters.questionType))
                return false;
            if (search) {
                const haystack = [
                    q.questionText,
                    ...(q.options || []).map((o) => o.optionText),
                    ...(q.childQuestions || []).map((c) => c.questionText),
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                if (!haystack.includes(search)) return false;
            }
            if (filters.recentOnly) {
                const { lastUpdatedOn } = getAudit(q);
                if (!lastUpdatedOn || !dayjs(lastUpdatedOn).isAfter(recentCutoff)) return false;
            }
            return true;
        });
    }, [questionsData, filters, recentCutoff]);

    const recentlyChangedCount = useMemo(
        () =>
            questionsData.filter((q) => {
                const { lastUpdatedOn } = getAudit(q);
                return lastUpdatedOn && dayjs(lastUpdatedOn).isAfter(recentCutoff);
            }).length,
        [questionsData, recentCutoff]
    );

    const pagedRows = useMemo(
        () => filteredRows.slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
        [filteredRows, pageIndex, pageSize]
    );

    const selectedRows = useMemo(
        () => filteredRows.filter((q) => selectedKeys.includes(getQuestionId(q))),
        [filteredRows, selectedKeys]
    );

    /* ------------------------------ actions ------------------------------ */

    const nextSortOrder = (targetModuleId) => {
        const inModule = questionsData.filter(
            (q) => String(q.moduleId) === String(targetModuleId)
        );
        return inModule.reduce((max, q) => Math.max(max, Number(q.sortOrder) || 0), 0) + 1;
    };

    const handleAddQuestion = () => {
        const targetModuleId = moduleId || filters.moduleId || moduleOptions[0]?.value || null;
        setFormState({
            open: true,
            mode: "add",
            data: targetModuleId
                ? { moduleId: targetModuleId, sortOrder: nextSortOrder(targetModuleId) }
                : null,
        });
    };

    const handleEdit = (record) => setFormState({ open: true, mode: "edit", data: record });

    // Saved in "add" mode, so buildQuestionPayload drops the question and option ids.
    const handleDuplicate = (record) => {
        setFormState({
            open: true,
            mode: "add",
            data: {
                ...record,
                questionText: `${record.questionText} (Copy)`,
                sortOrder: nextSortOrder(record.moduleId),
            },
        });
    };

    const handleDelete = async (record) => {
        setIsLoading(true);
        try {
            const { statusCode, message } = await deleteApi(DELETE_QUESTION, {
                params: { id: getQuestionId(record) },
            });
            if (statusCode === 200) {
                successToast(message);
                await reload();
            } else {
                errorToast(message);
            }
        } catch {
            errorToast("Failed to delete question");
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        setIsLoading(true);
        try {
            const { statusCode, message } = await postApi(DELETE_QUESTIONS_BULK, {
                questionIds: selectedRows.map(getQuestionId),
            });
            if (statusCode === 200) {
                successToast(message || `${selectedRows.length} questions deleted`);
                await reload();
            } else {
                errorToast(message);
            }
        } catch {
            errorToast("Failed to delete the selected questions");
        } finally {
            setIsLoading(false);
        }
    };

    const handleMove = async () => {
        const { rows, targetModuleId } = moveState;
        if (!targetModuleId) {
            errorToast("Please select a module");
            return;
        }
        setMoveState((prev) => ({ ...prev, saving: true }));
        let moved = 0;
        for (const row of rows) {
            const payload = {
                ...buildQuestionPayload(row, { moduleId: targetModuleId, keepIds: true }),
                questionId: getQuestionId(row),
            };
            const { statusCode } = await postApi(UPDATE_QUESTION, payload);
            if (statusCode === 200) moved += 1;
        }
        setMoveState({ open: false, rows: [], targetModuleId: null, saving: false });
        if (moved) successToast(`${moved} question${moved > 1 ? "s" : ""} moved`);
        if (moved < rows.length) errorToast(`${rows.length - moved} could not be moved`);
        await reload();
    };

    const handleExport = (rows, label) => {
        if (!rows.length) {
            errorToast("Nothing to export");
            return;
        }
        const sheet = XLSX.utils.json_to_sheet(toExportRows(rows, { syllabusNameById }));
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, "Questions");
        XLSX.writeFile(book, `Questions-${label || gradeName || `Grade-${gradeId}`}.xlsx`);
    };

    const copyText = async (record) => {
        try {
            await navigator.clipboard.writeText(record.questionText || "");
            successToast("Question text copied");
        } catch {
            errorToast("Could not copy");
        }
    };

    const openDrawer = (record, tab = "details") =>
        setDrawer({ open: true, question: record, tab });

    /* ------------------------------ columns ------------------------------ */

    const columns = useMemo(() => {
        const cols = [
            {
                title: "#",
                key: "index",
                width: 60,
                render: (_, __, index) => pageIndex * pageSize + index + 1,
            },
        ];

        if (!moduleId) {
            cols.push({
                title: "Module",
                dataIndex: "moduleName",
                key: "moduleName",
                width: 160,
                render: (text) => text || "-",
            });
        }

        cols.push(
            {
                title: "Question",
                dataIndex: "questionText",
                key: "questionText",
                render: (text, record) => (
                    <button
                        type="button"
                        className="questions-page__qtext"
                        title={text}
                        onClick={() => openDrawer(record)}
                    >
                        {text}
                    </button>
                ),
            },
            {
                title: "Type",
                dataIndex: "questionType",
                key: "questionType",
                width: 120,
                render: (val) => getTypeLabel(val),
            },
            {
                title: "Syllabus",
                dataIndex: "syllabusId",
                key: "syllabusId",
                width: 150,
                render: (val) => syllabusNameById[val] || "-",
            },
            {
                title: "Answer / Options",
                key: "answer",
                width: 170,
                render: (_, record) => (
                    <span className="questions-page__muted">{answerSummary(record)}</span>
                ),
            }
        );

        if (auditFieldsPresent) {
            cols.push({
                title: "Updated",
                key: "updated",
                width: 190,
                render: (_, record) => {
                    const { updateCount, lastUpdatedOn, lastUpdatedByName } = getAudit(record);
                    if (!updateCount) {
                        return <span className="questions-page__muted">No changes yet</span>;
                    }
                    return (
                        <Tooltip
                            title={`Last change ${formatDateTime(lastUpdatedOn)}${lastUpdatedByName ? ` by ${lastUpdatedByName}` : ""
                                }`}
                        >
                            <button
                                type="button"
                                className="questions-page__updated"
                                onClick={() => openDrawer(record, "history")}
                            >
                                <Tag color="gold">
                                    {updateCount} {updateCount === 1 ? "update" : "updates"}
                                </Tag>
                                <span className="questions-page__muted">
                                    {formatDateTime(lastUpdatedOn)}
                                </span>
                            </button>
                        </Tooltip>
                    );
                },
            });
        }

        cols.push({
            title: "Action",
            key: "action",
            width: 170,
            fixed: "right",
            render: (_, record) => {
                const menuItems = [];
                if (questionsPermission?.canAdd) {
                    menuItems.push({
                        key: "duplicate",
                        icon: <CopyOutlined />,
                        label: "Duplicate",
                        onClick: () => handleDuplicate(record),
                    });
                }
                if (questionsPermission?.canEdit && moduleOptions.length > 1) {
                    menuItems.push({
                        key: "move",
                        icon: <SwapOutlined />,
                        label: "Move to another module",
                        onClick: () =>
                            setMoveState({
                                open: true,
                                rows: [record],
                                targetModuleId: null,
                                saving: false,
                            }),
                    });
                }
                menuItems.push({
                    key: "copy",
                    icon: <CopyOutlined />,
                    label: "Copy question text",
                    onClick: () => copyText(record),
                });

                return (
                    <ActionGroup>
                        <ActionIcon
                            title="View full question"
                            icon={<EyeTwoTone twoToneColor={["#0e82fd", "#e6f4ff"]} />}
                            onClick={() => openDrawer(record)}
                        />
                        <ActionIcon
                            title="Change history"
                            icon={<HistoryOutlined style={{ color: "#722ed1", fontSize: 17 }} />}
                            onClick={() => openDrawer(record, "history")}
                        />
                        {questionsPermission?.canEdit && (
                            <ActionIcon
                                title="Edit"
                                icon={<EditTwoTone twoToneColor={["#52c41a", "#f6ffed"]} />}
                                onClick={() => handleEdit(record)}
                            />
                        )}
                        {questionsPermission?.canDelete && (
                            <ActionIcon
                                title="Delete"
                                icon={<DeleteTwoTone twoToneColor={["#ff4d4f", "#fff1f0"]} />}
                                confirm={{
                                    enabled: true,
                                    title: "Delete this question?",
                                    okText: "Delete",
                                    onConfirm: () => handleDelete(record),
                                }}
                            />
                        )}
                        <Dropdown
                            menu={{ items: menuItems }}
                            trigger={["click"]}
                            placement="bottomRight"
                        >
                            <ActionIcon
                                title="More"
                                icon={<MoreOutlined style={{ fontSize: 18 }} />}
                            />
                        </Dropdown>
                    </ActionGroup>
                );
            },
        });

        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        moduleId,
        auditFieldsPresent,
        syllabusNameById,
        moduleOptions,
        questionsData,
        pageIndex,
        pageSize,
    ]);

    /* ------------------------------- render ------------------------------ */

    const headerActions = (
        <div className="questions-page__headerActions">
            <ButtonComponent
                variant="secondary"
                onClick={() => handleExport(filteredRows)}
                disabled={!filteredRows.length}
            >
                <DownloadOutlined /> Export
            </ButtonComponent>
            {questionsPermission?.canAdd && (
                <ButtonComponent variant="primary" onClick={handleAddQuestion}>
                    <PlusOutlined /> Add Question
                </ButtonComponent>
            )}
        </div>
    );

    return (
        <div className="questions-page">
            {isLoading && <Loader />}
            <SubHeader
                title={moduleId ? "Questions" : "All Questions"}
                subTitle={currentSubTitle || gradeName}
                showBack={true}
                onBack={() => navigate(-1)}
                rightExtra={headerActions}
            />

            <div className="questions-page__body">
                <div className="questions-page__filters">
                    {!moduleId && (
                        <SelectInput
                            title="Module"
                            placeholder="All modules"
                            value={filters.moduleId || undefined}
                            options={moduleOptions}
                            showSearch
                            onChange={(val) => setFilter("module", val ?? "")}
                        />
                    )}
                    <SelectInput
                        title="Syllabus"
                        placeholder="All syllabus"
                        value={filters.syllabusId ? Number(filters.syllabusId) : undefined}
                        options={syllabusOptions}
                        showSearch
                        onChange={(val) => setFilter("syllabus", val ?? "")}
                    />
                    <SelectInput
                        title="Question type"
                        placeholder="All types"
                        value={filters.questionType ? Number(filters.questionType) : undefined}
                        options={QUESTION_TYPES}
                        onChange={(val) => setFilter("type", val ?? "")}
                    />
                    <div className="forminput">
                        <span className="input-label">Search</span>
                        <SearchInput
                            defaultValue={filters.search}
                            placeholder="Search question or option text"
                            onDebouncedChange={(val) => setFilter("q", val)}
                        />
                    </div>
                </div>

                <div className="questions-page__toolbar">
                    <div className="questions-page__counts">
                        <strong>{filteredRows.length}</strong> of {questionsData.length} questions
                        {auditFieldsPresent && recentlyChangedCount > 0 && (
                            <>
                                <span className="questions-page__dot">·</span>
                                <span>
                                    {recentlyChangedCount} changed in the last {RECENT_DAYS} days
                                </span>
                            </>
                        )}
                    </div>

                    <div className="questions-page__toolbarRight">
                        {auditFieldsPresent && (
                            <label className="questions-page__switch">
                                <Switch
                                    size="small"
                                    checked={filters.recentOnly}
                                    onChange={(checked) => setFilter("changed", checked ? "1" : "")}
                                />
                                Only recently changed
                            </label>
                        )}
                        {(filters.moduleId && !moduleId) ||
                            filters.syllabusId ||
                            filters.questionType ||
                            filters.search ||
                            filters.recentOnly ? (
                            <button
                                type="button"
                                className="questions-page__clear"
                                onClick={() => setSearchParams({}, { replace: true })}
                            >
                                Clear filters
                            </button>
                        ) : null}
                    </div>
                </div>

                {selectedRows.length > 0 && (
                    <div className="questions-page__bulkBar">
                        <span>
                            <strong>{selectedRows.length}</strong> selected
                        </span>
                        <div className="questions-page__bulkActions">
                            <ButtonComponent
                                variant="secondary"
                                onClick={() => handleExport(selectedRows, "Selected")}
                            >
                                <DownloadOutlined /> Export selected
                            </ButtonComponent>
                            {questionsPermission?.canEdit && moduleOptions.length > 1 && (
                                <ButtonComponent
                                    variant="secondary"
                                    onClick={() =>
                                        setMoveState({
                                            open: true,
                                            rows: selectedRows,
                                            targetModuleId: null,
                                            saving: false,
                                        })
                                    }
                                >
                                    <SwapOutlined /> Move to module
                                </ButtonComponent>
                            )}
                            {questionsPermission?.canDelete && (
                                <Popconfirm
                                    title={`Delete ${selectedRows.length} questions?`}
                                    okText="Delete"
                                    cancelText="Cancel"
                                    onConfirm={handleBulkDelete}
                                >
                                    <ButtonComponent variant="danger">
                                        <DeleteTwoTone twoToneColor={["#ffffff", "#ff4d4f"]} /> Delete
                                        selected
                                    </ButtonComponent>
                                </Popconfirm>
                            )}
                            <button
                                type="button"
                                className="questions-page__clear"
                                onClick={() => setSelectedKeys([])}
                            >
                                Clear selection
                            </button>
                        </div>
                    </div>
                )}

                <CustomTable
                    columns={columns}
                    dataSource={pagedRows}
                    loading={isLoading}
                    rowKey={(record) => getQuestionId(record)}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    total={filteredRows.length}
                    pageSizeOptions={[10, 20, 50, 100]}
                    rowSelection={{
                        selectedRowKeys: selectedKeys,
                        onChange: setSelectedKeys,
                        preserveSelectedRowKeys: true,
                    }}
                    expandable={{
                        expandedRowRender: (record) => (
                            <QuestionDetailView
                                question={record}
                                syllabusNameById={syllabusNameById}
                                compact
                            />
                        ),
                        rowExpandable: () => true,
                    }}
                    onPageChange={(current, size) => {
                        setPageIndex(size !== pageSize ? 0 : current - 1);
                        setPageSize(size);
                    }}
                />
            </div>

            <QuestionFormModal
                open={formState.open}
                mode={formState.mode}
                initialData={formState.data}
                defaultModuleId={moduleId || filters.moduleId || null}
                moduleOptions={moduleOptions}
                syllabusOptions={syllabusOptions}
                showModuleSelect={!moduleId}
                onClose={() => setFormState({ open: false, mode: "add", data: null })}
                onSaved={async () => {
                    setFormState({ open: false, mode: "add", data: null });
                    await reload();
                }}
            />

            <QuestionDetailDrawer
                open={drawer.open}
                question={drawer.question}
                syllabusNameById={syllabusNameById}
                historyEnabled
                canRestore={isAdmin}
                activeTab={drawer.tab}
                onTabChange={(tab) => setDrawer((prev) => ({ ...prev, tab }))}
                onClose={() => setDrawer({ open: false, question: null, tab: "details" })}
                onRestored={async () => {
                    // A restore creates a new current version, so the open drawer holds a stale one.
                    setDrawer({ open: false, question: null, tab: "details" });
                    await reload();
                }}
            />

            <CustomModal
                open={moveState.open}
                title={
                    moveState.rows.length > 1
                        ? `Move ${moveState.rows.length} questions`
                        : "Move question"
                }
                onClose={() => setMoveState({ open: false, rows: [], targetModuleId: null, saving: false })}
                primaryText="Move"
                onPrimary={handleMove}
                primaryProps={{ loading: moveState.saving }}
            >
                <p className="questions-page__muted" style={{ marginTop: 0 }}>
                    The question keeps its text, options and history. Only the module changes.
                </p>
                <SelectInput
                    title="Move to module"
                    placeholder="Select module"
                    options={moduleOptions.filter(
                        (m) =>
                            !moveState.rows.every((r) => String(r.moduleId) === String(m.value))
                    )}
                    value={moveState.targetModuleId}
                    showSearch
                    onChange={(val) => setMoveState((prev) => ({ ...prev, targetModuleId: val }))}
                    required
                />
            </CustomModal>
        </div>
    );
};

export default Questions;
